import React, { useState, useMemo } from "react";
import { inr } from "../data/properties";

const BANKS = [
  { name: "HDFC Bank", rate: 8.35 },
  { name: "State Bank of India", rate: 8.40 },
  { name: "ICICI Bank", rate: 8.45 },
  { name: "Axis Bank", rate: 8.55 },
  { name: "Kotak Mahindra", rate: 8.60 },
];

function emi(principal, annualRate, years) {
  const r = annualRate / 12 / 100;
  const n = years * 12;
  if (r === 0) return principal / n;
  return (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

export default function EMICalculator({ price = 48500000, dark = false }) {
  const [downPct, setDownPct] = useState(20);
  const [years, setYears] = useState(20);
  const [bankIdx, setBankIdx] = useState(0);

  const bank = BANKS[bankIdx];
  const down = Math.round((price * downPct) / 100);
  const principal = price - down;

  const monthly = useMemo(() => emi(principal, bank.rate, years), [principal, bank.rate, years]);
  const totalPay = monthly * years * 12;
  const interest = totalPay - principal;

  const wrap = dark
    ? { background: "var(--obsidian-soft)", border: "1px solid var(--obsidian-line)", color: "var(--paper-ink)" }
    : { background: "#fff", border: "1px solid var(--hairline-ultra)" };

  return (
    <div style={{ borderRadius: 3, padding: "clamp(32px,4vw,48px)", ...wrap }} className={dark ? "dark" : ""}>
      <div className="spaced" style={{ marginBottom: 32, flexDirection: "column", alignItems: "flex-start", gap: 16 }}>
        <div>
          <span className="eyebrow gold">FinTech · Native EMI</span>
          <h3 className="h-md" style={{ marginTop: 16 }}>Structure your monthly outlay</h3>
        </div>
        <span className="badge"><span className="dot" /> Bank-Assisted</span>
      </div>

      <div className="field" style={{ marginBottom: 28 }}>
        <label>Lending partner · pre-negotiated rate</label>
        <select value={bankIdx} onChange={(e) => setBankIdx(Number(e.target.value))}>
          {BANKS.map((b, i) => (
            <option key={b.name} value={i}>{b.name} — {b.rate}% p.a.</option>
          ))}
        </select>
      </div>

      <div className="field" style={{ marginBottom: 12 }}>
        <label>Down payment — {downPct}% (₹{inr(down)})</label>
        <input type="range" min="10" max="60" value={downPct} onChange={(e) => setDownPct(Number(e.target.value))} />
      </div>
      <div className="field" style={{ marginBottom: 32 }}>
        <label>Tenure — {years} years</label>
        <input type="range" min="5" max="30" value={years} onChange={(e) => setYears(Number(e.target.value))} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 0, borderTop: `1px solid ${dark ? "var(--obsidian-line)" : "var(--hairline-soft)"}`, paddingTop: 32 }}>
        <div className="spaced" style={{ flexDirection: "column", alignItems: "flex-start", gap: 14 }}>
          <span className="muted" style={{ fontSize: "0.78rem", letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 500 }}>Estimated monthly EMI</span>
          <span style={{ fontFamily: "var(--serif)", fontSize: "clamp(2.2rem,5vw,3.2rem)", color: "var(--champagne)", lineHeight: 1 }} className="mono-num">
            ₹{inr(Math.round(monthly))}
          </span>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24, marginTop: 32 }}>
        {[
          ["Loan principal", `₹${inr(principal)}`],
          ["Total interest", `₹${inr(Math.round(interest))}`],
          ["Total payable", `₹${inr(Math.round(totalPay))}`],
        ].map(([k, v]) => (
          <div key={k}>
            <div className="muted" style={{ fontSize: "0.70rem", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 600, marginBottom: 8 }}>{k}</div>
            <div className="mono-num" style={{ fontWeight: 600, fontSize: "1.05rem" }}>{v}</div>
          </div>
        ))}
      </div>

      <button className="btn btn-gold" style={{ width: "100%", justifyContent: "center", marginTop: 36 }}>
        Get Pre-Qualified with {bank.name.split(" ")[0]} <span className="arr">→</span>
      </button>
      <p className="muted" style={{ fontSize: "0.73rem", marginTop: 18, textAlign: "center", lineHeight: 1.7 }}>
        Indicative only. Final rate subject to lender credit assessment. Aditi routes your file directly to the bank's priority desk.
      </p>
    </div>
  );
}
